import { inject, Injectable } from '@angular/core';
import { StorageService } from './storage/storage.service';
export interface WeatherData {
  temperature: number;        // Durchschnittstemperatur in °C
  precipitation: number;      // Niederschlag in mm
  cloudCover: number;         // Bewölkung in %
  weatherCode: number;        // WMO Wettercode
  windSpeed: number;          // Windgeschwindigkeit in km/h
  windDirection: number;      // Windrichtung in Grad
  humidity: number;           // Relative Luftfeuchtigkeit in %
  precipitationLast3Days: number;  // Niederschlag der letzten 3 Tage in mm (für Bodenzustand)
}

export interface WeatherDescription {
  temperature: string;        // z.B. "kühl (12°C)"
  moisture: string;           // z.B. "trocken", "feucht"
  sky: string;                // z.B. "heiter", "leicht bewölkt"
  wind: string;               // z.B. "leichter Wind aus SW"
  humidity: string;           // z.B. "Luftfeuchte 65%"
  soil: string;               // z.B. "Boden trocken"
  full: string;               // Kombinierter Text
}

// Konfigurierbare Schwellwerte für Wetterinterpretation
export interface WeatherConfig {
  temperature: {
    frostig: number;    // < frostig
    kalt: number;       // < kalt
    kuehl: number;      // < kühl
    mild: number;       // < mild
    warm: number;       // < warm
    // >= warm = heiß
  };
  precipitation: {
    trocken: number;       // < trocken
    leichtFeucht: number;  // < leicht feucht
    feucht: number;        // < feucht
    // >= feucht = nach Regenperiode
  };
  cloudCover: {
    heiter: number;        // < heiter
    leichtBewoelkt: number; // < leicht bewölkt
    bewoelkt: number;      // < bewölkt
    // >= bewölkt = bedeckt
  };
  windSpeed: {
    windstill: number;     // < windstill
    leicht: number;        // < leicht
    maessig: number;       // < mäßig
    stark: number;         // < stark
    // >= stark = stürmisch
  };
  humidity: {
    sehrTrocken: number;   // < sehr trocken
    normal: number;        // < normal
    feucht: number;        // < feucht
    // >= feucht = hohe Luftfeuchte/schwül
    schwuelMinTemp: number; // Mindesttemperatur für "schwül"
  };
  soil: {
    trocken: number;       // < trocken
    leichtFeucht: number;  // < leicht feucht
    feucht: number;        // < feucht
    nass: number;          // < nass
    // >= nass = durchnässt
    tage: number;          // Anzahl Tage für Niederschlagsberechnung (Standard: 3)
  };
}

// Standard-Konfiguration
export const DEFAULT_WEATHER_CONFIG: WeatherConfig = {
  temperature: {
    frostig: 0,
    kalt: 5,
    kuehl: 12,
    mild: 18,
    warm: 25
  },
  precipitation: {
    trocken: 1,
    leichtFeucht: 5,
    feucht: 10
  },
  cloudCover: {
    heiter: 20,
    leichtBewoelkt: 50,
    bewoelkt: 80
  },
  windSpeed: {
    windstill: 5,
    leicht: 20,
    maessig: 40,
    stark: 60
  },
  humidity: {
    sehrTrocken: 30,
    normal: 60,
    feucht: 80,
    schwuelMinTemp: 18
  },
  soil: {
    trocken: 1,
    leichtFeucht: 5,
    feucht: 15,
    nass: 30,
    tage: 3
  }
};

@Injectable({
  providedIn: 'root'
})
export class WeatherService {
  private storage = inject(StorageService);
  private config: WeatherConfig = DEFAULT_WEATHER_CONFIG;
  private configLoaded = false;

  constructor() {
    this.loadConfig();
  }

  /**
   * Lädt die Konfiguration aus dem Storage
   */
  private async loadConfig() {
    try {
      const savedConfig = await this.storage.get('weatherConfig');
      if (savedConfig) {
        this.config = { ...DEFAULT_WEATHER_CONFIG, ...savedConfig };
      }
      this.configLoaded = true;
    } catch (e) {
      console.error('[WeatherService] Fehler beim Laden der Konfiguration:', e);
      this.configLoaded = true;
    }
  }

  /**
   * Gibt die aktuelle Konfiguration zurück
   */
  getConfig(): WeatherConfig {
    return this.config;
  }

  /**
   * Speichert die Konfiguration
   */
  async saveConfig(config: WeatherConfig): Promise<void> {
    this.config = config;
    await this.storage.set('weatherConfig', config);
  }

  /**
   * Setzt die Konfiguration auf Standardwerte zurück
   */
  async resetConfig(): Promise<void> {
    this.config = { ...DEFAULT_WEATHER_CONFIG };
    await this.storage.remove('weatherConfig');
  }

  /**
   * Ruft historische Wetterdaten von Open-Meteo ab
   * @param lat Breitengrad
   * @param lon Längengrad
   * @param date Datum im Format YYYY-MM-DD
   */
  async getHistoricalWeather(lat: number, lon: number, date: string): Promise<WeatherData | null> {
    try {
      // Berechne Start-Datum basierend auf konfigurierten Tagen für Bodenfeuchtigkeit
      const soilDays = this.config.soil.tage || 3;
      const targetDate = new Date(date);
      const startDateSoil = new Date(targetDate);
      startDateSoil.setDate(startDateSoil.getDate() - (soilDays - 1));
      const startDateSoilStr = this.formatDateForApi(startDateSoil);

      // Open-Meteo Historical Weather API (kostenlos, kein API-Key)
      const url = `https://archive-api.open-meteo.com/v1/archive?` +
        `latitude=${lat}&longitude=${lon}` +
        `&start_date=${startDateSoilStr}&end_date=${date}` +
        `&daily=temperature_2m_mean,precipitation_sum,weather_code,wind_speed_10m_max,wind_direction_10m_dominant` +
        `&hourly=cloud_cover,relative_humidity_2m` +
        `&timezone=Europe/Berlin`;

      const response = await fetch(url);
      if (!response.ok) {
        console.error('[WeatherService] API-Fehler:', response.status);
        return null;
      }

      const data = await response.json();

      if (!data.daily || !data.daily.temperature_2m_mean) {
        console.error('[WeatherService] Keine Daten für dieses Datum');
        return null;
      }

      // Finde den Index des Zieldatums (letzter Tag in der Abfrage)
      const dayIndex = data.daily.time.indexOf(date);
      if (dayIndex === -1) {
        console.error('[WeatherService] Zieldatum nicht gefunden');
        return null;
      }

      // Durchschnittliche Bewölkung und Luftfeuchtigkeit aus Stundenwerten des Zieldatums
      const hoursPerDay = 24;
      const startHour = dayIndex * hoursPerDay;
      const endHour = startHour + hoursPerDay;

      const cloudCoverValues = (data.hourly?.cloud_cover || []).slice(startHour, endHour);
      const avgCloudCover = cloudCoverValues.length > 0
        ? cloudCoverValues.reduce((a: number, b: number) => a + b, 0) / cloudCoverValues.length
        : 50;

      const humidityValues = (data.hourly?.relative_humidity_2m || []).slice(startHour, endHour);
      const avgHumidity = humidityValues.length > 0
        ? humidityValues.reduce((a: number, b: number) => a + b, 0) / humidityValues.length
        : 50;

      // Niederschlag der konfigurierten Tage summieren
      const precipitationSoilDays = (data.daily.precipitation_sum || [])
        .reduce((a: number, b: number) => a + (b || 0), 0);

      return {
        temperature: data.daily.temperature_2m_mean[dayIndex],
        precipitation: data.daily.precipitation_sum[dayIndex] || 0,
        cloudCover: avgCloudCover,
        weatherCode: data.daily.weather_code[dayIndex] || 0,
        windSpeed: data.daily.wind_speed_10m_max[dayIndex] || 0,
        windDirection: data.daily.wind_direction_10m_dominant[dayIndex] || 0,
        humidity: avgHumidity,
        precipitationLast3Days: precipitationSoilDays
      };
    } catch (error) {
      console.error('[WeatherService] Fehler beim Abrufen:', error);
      return null;
    }
  }

  /**
   * Ruft aktuelles Wetter von Open-Meteo ab
   */
  async getCurrentWeather(lat: number, lon: number): Promise<WeatherData | null> {
    try {
      // Konfigurierbare Tage für Bodenfeuchtigkeit
      const soilDays = this.config.soil.tage || 3;
      const pastDays = soilDays - 1; // -1 weil heute mitgezählt wird

      const url = `https://api.open-meteo.com/v1/forecast?` +
        `latitude=${lat}&longitude=${lon}` +
        `&current=temperature_2m,precipitation,weather_code,cloud_cover,wind_speed_10m,wind_direction_10m,relative_humidity_2m` +
        `&daily=precipitation_sum` +
        `&past_days=${pastDays}` +
        `&forecast_days=1` +
        `&timezone=Europe/Berlin`;

      const response = await fetch(url);
      if (!response.ok) {
        console.error('[WeatherService] API-Fehler:', response.status);
        return null;
      }

      const data = await response.json();

      if (!data.current) {
        console.error('[WeatherService] Keine aktuellen Daten');
        return null;
      }

      // Niederschlag der konfigurierten Tage summieren
      const precipitationSoilDays = (data.daily?.precipitation_sum || [])
        .reduce((a: number, b: number) => a + (b || 0), 0);

      return {
        temperature: data.current.temperature_2m,
        precipitation: data.current.precipitation || 0,
        cloudCover: data.current.cloud_cover || 0,
        weatherCode: data.current.weather_code || 0,
        windSpeed: data.current.wind_speed_10m || 0,
        windDirection: data.current.wind_direction_10m || 0,
        humidity: data.current.relative_humidity_2m || 50,
        precipitationLast3Days: precipitationSoilDays
      };
    } catch (error) {
      console.error('[WeatherService] Fehler beim Abrufen:', error);
      return null;
    }
  }

  /**
   * Wandelt Wetterdaten in lesbare Beschreibung um (mit konfigurierbaren Schwellwerten)
   */
  describeWeather(weather: WeatherData): WeatherDescription {
    const cfg = this.config;

    // Temperatur beschreiben (mit Gradangabe)
    let tempLabel: string;
    if (weather.temperature < cfg.temperature.frostig) {
      tempLabel = 'frostig';
    } else if (weather.temperature < cfg.temperature.kalt) {
      tempLabel = 'kalt';
    } else if (weather.temperature < cfg.temperature.kuehl) {
      tempLabel = 'kühl';
    } else if (weather.temperature < cfg.temperature.mild) {
      tempLabel = 'mild';
    } else if (weather.temperature < cfg.temperature.warm) {
      tempLabel = 'warm';
    } else {
      tempLabel = 'heiß';
    }
    const temperature = `${tempLabel} (${Math.round(weather.temperature)}°C)`;

    // Feuchtigkeit/Niederschlag beschreiben
    let moisture: string;
    if (weather.precipitation >= cfg.precipitation.feucht) {
      moisture = 'nach Regenperiode';
    } else if (weather.precipitation >= cfg.precipitation.leichtFeucht) {
      moisture = 'feucht';
    } else if (weather.precipitation >= cfg.precipitation.trocken) {
      moisture = 'leicht feucht';
    } else {
      moisture = 'trocken';
    }

    // Himmel/Bewölkung beschreiben
    let sky: string;
    if (weather.cloudCover < cfg.cloudCover.heiter) {
      sky = 'heiter';
    } else if (weather.cloudCover < cfg.cloudCover.leichtBewoelkt) {
      sky = 'leicht bewölkt';
    } else if (weather.cloudCover < cfg.cloudCover.bewoelkt) {
      sky = 'bewölkt';
    } else {
      sky = 'bedeckt';
    }

    // WMO-Wettercode für spezielle Bedingungen
    const weatherCodeDescriptions: Record<number, string> = {
      45: 'neblig',
      48: 'Reifnebel',
      51: 'leichter Nieselregen',
      53: 'Nieselregen',
      55: 'starker Nieselregen',
      61: 'leichter Regen',
      63: 'Regen',
      65: 'starker Regen',
      71: 'leichter Schneefall',
      73: 'Schneefall',
      75: 'starker Schneefall',
      80: 'Regenschauer',
      81: 'starke Regenschauer',
      85: 'Schneeschauer',
      95: 'Gewitter',
    };

    // Spezielle Wettercodes überschreiben ggf. die Beschreibung
    if (weatherCodeDescriptions[weather.weatherCode]) {
      sky = weatherCodeDescriptions[weather.weatherCode];
    }

    // Wind beschreiben
    let wind: string;
    const windDir = this.getWindDirection(weather.windDirection);
    if (weather.windSpeed < cfg.windSpeed.windstill) {
      wind = 'windstill';
    } else if (weather.windSpeed < cfg.windSpeed.leicht) {
      wind = `leichter Wind aus ${windDir}`;
    } else if (weather.windSpeed < cfg.windSpeed.maessig) {
      wind = `mäßiger Wind aus ${windDir}`;
    } else if (weather.windSpeed < cfg.windSpeed.stark) {
      wind = `starker Wind aus ${windDir}`;
    } else {
      wind = `stürmisch aus ${windDir}`;
    }

    // Luftfeuchtigkeit beschreiben (schwül nur bei Wärme)
    let humidity: string;
    if (weather.humidity < cfg.humidity.sehrTrocken) {
      humidity = 'sehr trockene Luft';
    } else if (weather.humidity < cfg.humidity.normal) {
      humidity = 'normale Luftfeuchte';
    } else if (weather.humidity < cfg.humidity.feucht) {
      humidity = 'feuchte Luft';
    } else if (weather.temperature >= cfg.humidity.schwuelMinTemp) {
      humidity = 'schwül';
    } else {
      humidity = 'hohe Luftfeuchte';
    }

    // Bodenzustand beschreiben (basierend auf Niederschlag der letzten 3 Tage)
    let soil: string;
    if (weather.precipitationLast3Days < cfg.soil.trocken) {
      soil = 'Boden trocken';
    } else if (weather.precipitationLast3Days < cfg.soil.leichtFeucht) {
      soil = 'Boden leicht feucht';
    } else if (weather.precipitationLast3Days < cfg.soil.feucht) {
      soil = 'Boden feucht';
    } else if (weather.precipitationLast3Days < cfg.soil.nass) {
      soil = 'Boden nass';
    } else {
      soil = 'Boden durchnässt';
    }

    const full = `${temperature}, ${moisture}, ${sky}, ${wind}, ${humidity}, ${soil}`;

    return { temperature, moisture, sky, wind, humidity, soil, full };
  }

  /**
   * Wandelt Windrichtung (Grad) in Himmelsrichtung um
   */
  private getWindDirection(degrees: number): string {
    const directions = ['N', 'NO', 'O', 'SO', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
  }

  /**
   * Interpretiert gespeicherte Rohdaten mit aktuellen Schwellwerten neu
   * @param rohdaten Die gespeicherten Rohdaten aus WitterungRohdaten
   * @returns Neue Beschreibung basierend auf aktuellen Schwellwerten
   */
  reinterpretRohdaten(rohdaten: {
    temperature: number;
    precipitation: number;
    cloudCover: number;
    weatherCode: number;
    windSpeed: number;
    windDirection: number;
    humidity: number;
    precipitationDays: number;
  }): WeatherDescription {
    // Konvertiere Rohdaten in WeatherData-Format
    const weatherData: WeatherData = {
      temperature: rohdaten.temperature,
      precipitation: rohdaten.precipitation,
      cloudCover: rohdaten.cloudCover,
      weatherCode: rohdaten.weatherCode,
      windSpeed: rohdaten.windSpeed,
      windDirection: rohdaten.windDirection,
      humidity: rohdaten.humidity,
      precipitationLast3Days: rohdaten.precipitationDays
    };
    return this.describeWeather(weatherData);
  }

  /**
   * Hilfsfunktion: Datum als YYYY-MM-DD formatieren
   */
  formatDateForApi(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
